import {
    Box,
    Button,
    CircularProgress,
    Grid,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { format, parseISO, startOfMonth } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import api from '../../api';
import { normalizeJobsDateRange } from '../../helpers/jobs';
import { patchOverviewCardsForNotification } from '../../helpers/jobLivePatch';
import { seedJobLiveInProgress } from '../../helpers/jobLiveInProgress';
import {
    buildPrinterOverviewCards,
    buildPrinterStatsQuery,
    defaultPrintersOverviewPeriod,
    PRINTERS_OVERVIEW_PERIOD_MODES,
    printersInProgressSet,
    resolvePrintersOverviewPeriod,
    summarizeMaintenancesByPrinter,
} from '../../helpers/printersOverview';
import { applyPrinterRuntimeFlags, loadIdlePrinterMap, refreshPrinterIdleFlags } from '../../helpers/idlePrinters';
import { openRestrictionForPrinter } from '../../helpers/printerProductivity';
import { isJobIntegrationLiveNotification, subscribeLiveNotifications } from '../../helpers/liveNotifications';
import { useLiveUpdateHighlights } from '../../helpers/liveUpdateHighlight';
import { useTickingNow } from '../../hooks/useTickingNow';
import { sortPrintersByName } from '../../helpers/printers';
import Loading from '../Loading';
import PrinterOverviewCard from './PrinterOverviewCard';
import PrinterProductivityDialog from './PrinterProductivityDialog';
import PrintersOverviewTable from './PrintersOverviewTable';

function matchesPrinterSearch(printer, searchQuery) {
    const q = String(searchQuery || '').trim().toLowerCase();
    if (!q) {
        return true;
    }
    const haystack = [
        printer.name,
        printer.brand,
        printer.model,
        printer.printer_uuid,
        printer.id,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    return haystack.includes(q);
}

export default function PrintersOverviewPanel({
    viewMode = 'list',
    showArchived = false,
    searchQuery = '',
    onArchivedTotalChange,
    onEdit,
    onMaintenances,
    onDelete,
    onCopyUuid,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const defaultPeriod = useMemo(() => defaultPrintersOverviewPeriod(), []);
    const defaultCustomPeriod = useMemo(() => {
        const today = new Date();
        return {
            from: format(startOfMonth(today), 'yyyy-MM-dd'),
            to: format(today, 'yyyy-MM-dd'),
        };
    }, []);

    const [periodFrom, setPeriodFrom] = useState(defaultPeriod.from);
    const [periodTo, setPeriodTo] = useState(defaultPeriod.to);
    const [appliedPeriod, setAppliedPeriod] = useState(defaultPeriod);
    const [cardsPeriod, setCardsPeriod] = useState(defaultPeriod);
    const [periodMode, setPeriodMode] = useState('panel_period');
    const [customFrom, setCustomFrom] = useState(defaultCustomPeriod.from);
    const [customTo, setCustomTo] = useState(defaultCustomPeriod.to);
    const [loading, setLoading] = useState(true);
    const [cardsLoading, setCardsLoading] = useState(false);
    const [cards, setCards] = useState([]);
    const [restrictions, setRestrictions] = useState([]);
    const [productivityPrinter, setProductivityPrinter] = useState(null);
    const isInitialLoad = useRef(true);
    const { isPrinterHighlighted } = useLiveUpdateHighlights(isJobIntegrationLiveNotification);

    const loadOverview = useCallback((mode, panelPeriod, customPeriodFrom, customPeriodTo) => {
        const resolved = resolvePrintersOverviewPeriod({
            mode,
            appliedPeriod: panelPeriod,
            customFrom: customPeriodFrom,
            customTo: customPeriodTo,
        });
        if (resolved.error) {
            Swal.fire('Período inválido', resolved.error, 'warning');
            return Promise.resolve();
        }

        const statsQuery = buildPrinterStatsQuery({
            finishedFrom: resolved.finishedFrom,
            finishedTo: resolved.finishedTo,
        });

        if (isInitialLoad.current) {
            setLoading(true);
        } else {
            setCardsLoading(true);
        }

        return Promise.all([
            api.get('printers?page=1&limit=200'),
            api.get(`jobs/printer-stats?${statsQuery}`),
            api.get('jobs?status=em_andamento&limit=200'),
            api.get('printer-maintenances?page=1&limit=500'),
            api.get('printer-productivity-restrictions'),
        ])
            .then(async ([printersRes, statsRes, inProgressRes, maintenancesRes, restrictionsRes]) => {
                const printers = sortPrintersByName(printersRes.data.printers || []);
                const stats = statsRes.data.data?.printers || [];
                const inProgressJobs = inProgressRes.data.data?.jobs || [];
                const maintenances = maintenancesRes.data.data?.maintenances || [];
                const restrictionRows = restrictionsRes.data?.restrictions || [];
                const maintenanceSummary = summarizeMaintenancesByPrinter(
                    maintenances,
                    resolved.finishedFrom,
                    resolved.finishedTo,
                );
                const inProgressSet = printersInProgressSet(inProgressJobs);
                seedJobLiveInProgress(inProgressJobs);

                let activityMap = new Map();
                try {
                    ({ activityMap } = await loadIdlePrinterMap(api, printers, inProgressJobs, restrictionRows));
                } catch {
                    activityMap = new Map();
                }

                setRestrictions(restrictionRows);
                setCards(
                    applyPrinterRuntimeFlags(
                        buildPrinterOverviewCards(printers, stats, maintenanceSummary, inProgressSet),
                        {
                            printingSet: inProgressSet,
                            activityMap,
                        },
                    ).map((card) => ({
                        ...card,
                        productivity_restriction: openRestrictionForPrinter(
                            restrictionRows,
                            card.printer_uuid || card.id,
                        ),
                    })),
                );
                setCardsPeriod(resolved.period);
            })
            .catch(() => {
                Swal.fire('Erro', 'Não foi possível carregar o painel de impressoras.', 'error');
            })
            .finally(() => {
                setCardsLoading(false);
                if (isInitialLoad.current) {
                    isInitialLoad.current = false;
                    setLoading(false);
                }
            });
    }, []);

    useEffect(() => {
        if (periodMode !== 'custom') {
            loadOverview(periodMode, appliedPeriod, customFrom, customTo);
        }
    }, [periodMode, appliedPeriod, loadOverview]);

    useEffect(() => {
        return subscribeLiveNotifications((detail) => {
            if (!isJobIntegrationLiveNotification(detail)) {
                return;
            }
            setCards((prev) => patchOverviewCardsForNotification(prev, detail));
        });
    }, []);

    const now = useTickingNow(15000);
    const liveCards = useMemo(
        () => refreshPrinterIdleFlags(cards, now),
        [cards, now],
    );

    const archivedTotal = useMemo(
        () => liveCards.filter((printer) => printer.active === false).length,
        [liveCards],
    );

    useEffect(() => {
        onArchivedTotalChange?.(archivedTotal);
    }, [archivedTotal, onArchivedTotalChange]);

    const visibleCards = useMemo(() => {
        return liveCards.filter((printer) => {
            const isArchived = printer.active === false;
            if (showArchived ? !isArchived : isArchived) {
                return false;
            }
            return matchesPrinterSearch(printer, searchQuery);
        });
    }, [liveCards, showArchived, searchQuery]);

    const highlightedMap = useMemo(() => {
        const map = {};
        visibleCards.forEach((printer) => {
            const id = printer.printer_uuid || printer.id;
            if (id && isPrinterHighlighted(id)) {
                map[id] = true;
            }
        });
        return map;
    }, [visibleCards, isPrinterHighlighted]);

    const handleApplyPanelPeriod = () => {
        const normalized = normalizeJobsDateRange(periodFrom, periodTo);
        setPeriodFrom(normalized.start);
        setPeriodTo(normalized.end);
        setAppliedPeriod({ from: normalized.start, to: normalized.end });
    };

    const handlePeriodModeChange = (_event, value) => {
        if (value) {
            setPeriodMode(value);
        }
    };

    const handleCustomDateFromChange = (value) => {
        setCustomFrom(value);
        if (!value) {
            return;
        }
        const normalized = normalizeJobsDateRange(value, customTo || value);
        setCustomFrom(normalized.start);
        setCustomTo(normalized.end);
    };

    const handleCustomDateToChange = (value) => {
        setCustomTo(value);
        if (!value) {
            return;
        }
        const normalized = normalizeJobsDateRange(customFrom || value, value);
        setCustomFrom(normalized.start);
        setCustomTo(normalized.end);
    };

    const handleApplyCustomPeriod = () => {
        loadOverview('custom', appliedPeriod, customFrom, customTo);
    };

    const handleDeleteAndRefresh = (printerId) => {
        Promise.resolve(onDelete?.(printerId)).then((removed) => {
            if (removed === false) {
                return;
            }
            setCards((prev) => prev.filter((printer) => (printer.printer_uuid || printer.id) !== printerId));
        });
    };

    const panelPeriodLabel = useMemo(() => {
        try {
            const from = format(parseISO(appliedPeriod.from), 'dd/MM/yyyy');
            const to = format(parseISO(appliedPeriod.to), 'dd/MM/yyyy');
            return `${from} — ${to}`;
        } catch {
            return '';
        }
    }, [appliedPeriod]);

    const cardsPeriodLabel = useMemo(() => {
        if (periodMode === 'all_time') {
            return 'Todo o tempo';
        }
        if (!cardsPeriod.from || !cardsPeriod.to) {
            return '';
        }
        try {
            const from = format(parseISO(cardsPeriod.from), 'dd/MM/yyyy');
            const to = format(parseISO(cardsPeriod.to), 'dd/MM/yyyy');
            return `${from} — ${to}`;
        } catch {
            return '';
        }
    }, [periodMode, cardsPeriod]);

    if (loading) {
        return <Loading />;
    }

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2,
                }}
            >
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        {showArchived ? 'Impressoras arquivadas' : 'Impressoras'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Período do filtro: {panelPeriodLabel}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <TextField
                        label="De"
                        type="date"
                        size="small"
                        value={periodFrom}
                        onChange={(e) => setPeriodFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Até"
                        type="date"
                        size="small"
                        value={periodTo}
                        onChange={(e) => setPeriodTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button variant="contained" onClick={handleApplyPanelPeriod}>
                        Atualizar
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 1.5,
                }}
            >
                <Typography variant="subtitle1" fontWeight={600}>
                    Métricas do período
                </Typography>
                <ToggleButtonGroup
                    size="small"
                    color="primary"
                    exclusive
                    value={periodMode}
                    onChange={handlePeriodModeChange}
                    sx={{ flexWrap: 'wrap' }}
                >
                    {PRINTERS_OVERVIEW_PERIOD_MODES.map((option) => (
                        <ToggleButton key={option.value} value={option.value}>
                            {option.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {periodMode === 'panel_period' && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Usa o período do filtro acima
                    {panelPeriodLabel ? ` (${panelPeriodLabel})` : ''}
                    .
                </Typography>
            )}

            {periodMode === 'custom' && (
                <Grid container spacing={2} sx={{ mb: 1.5 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Data inicial"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={customFrom}
                            onChange={(e) => handleCustomDateFromChange(e.target.value)}
                            inputProps={{ max: customTo || undefined }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Data final"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={customTo}
                            onChange={(e) => handleCustomDateToChange(e.target.value)}
                            inputProps={{ min: customFrom || undefined }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            variant="contained"
                            onClick={handleApplyCustomPeriod}
                            disabled={cardsLoading}
                        >
                            Atualizar período
                        </Button>
                    </Grid>
                </Grid>
            )}

            {cardsPeriodLabel && periodMode !== 'panel_period' && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Exibindo: {cardsPeriodLabel}
                </Typography>
            )}

            {cardsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={28} />
                </Box>
            )}

            {visibleCards.length === 0 ? (
                <Typography color="text.secondary" sx={{ mb: 2, opacity: cardsLoading ? 0.5 : 1 }}>
                    {showArchived
                        ? 'Nenhuma impressora arquivada com os filtros atuais.'
                        : 'Nenhuma impressora ativa encontrada. Cadastre uma nova ou ajuste a busca.'}
                </Typography>
            ) : viewMode === 'list' ? (
                <Box sx={{ opacity: cardsLoading ? 0.5 : 1, mb: 2 }}>
                    <PrintersOverviewTable
                        printers={visibleCards}
                        highlightedPrinterIds={highlightedMap}
                        onEdit={onEdit}
                        onMaintenances={onMaintenances}
                        onDelete={handleDeleteAndRefresh}
                        onCopyUuid={onCopyUuid}
                        onProductivity={setProductivityPrinter}
                    />
                </Box>
            ) : (
                <Grid container spacing={2} sx={{ opacity: cardsLoading ? 0.5 : 1, mb: 2 }}>
                    {visibleCards.map((printer) => (
                        <Grid item xs={12} sm={6} md={3} key={printer.printer_uuid || printer.id}>
                            <PrinterOverviewCard
                                printer={printer}
                                period={cardsPeriod}
                                highlighted={isPrinterHighlighted(printer.printer_uuid || printer.id)}
                                onEdit={onEdit}
                                onMaintenances={onMaintenances}
                                onDelete={handleDeleteAndRefresh}
                                onCopyUuid={onCopyUuid}
                                onProductivity={setProductivityPrinter}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}
            <PrinterProductivityDialog
                open={Boolean(productivityPrinter)}
                printer={productivityPrinter}
                restrictions={restrictions}
                onClose={() => setProductivityPrinter(null)}
                onChanged={() => {
                    loadOverview(periodMode, appliedPeriod, customFrom, customTo);
                }}
            />
        </Box>
    );
}
